
'use client';

import { JartinetHermandad, SocialPost, SocialStory, UserProfile, Banda, Organizacion, SocialComment, HighlightStoryGroup } from '@/lib/types';
import Image from '@/components/ui/Image';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { MoreHorizontal, Play, Volume2, VolumeX, Newspaper, Copy, Search, Link as LinkIcon, Handshake, Hourglass, Check, X, Heart, MessageCircle, Send, Trash2, Trophy, Medal, Pause } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import { useMemo, useState, useRef, useEffect, useTransition, useCallback } from 'react';
import { StoryViewer } from '@/components/social/StoryViewer';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter, SheetClose } from '@/components/ui/sheet';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious, type CarouselApi } from '@/components/ui/carousel';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { useToast } from '@/hooks/use-toast';
import { Input } from '@/components/ui/input';
import { acceptCollaboration, rejectCollaboration, toggleLikePost, createSocialPost, addCommentToPost, deleteCommentFromPost, sharePostToStory, getStoriesByIds } from '@/lib/actions';
import { cn } from '@/lib/utils';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { Slider } from '../ui/slider';
import { HighlightStoryViewer } from './HighlightStoryViewer';
import { motion } from 'framer-motion';
import { Sparkles, Music, Lock } from 'lucide-react';
import { SocialContext, useSocial, MentionProfile } from './social-context';
import { SocialText } from './SocialText';


// --- Types ---

export type StoryGroup = {
  id: string;
  type: 'hermandad' | 'banda' | 'organizacion';
  name: string;
  shield: string;
  stories: SocialStory[];
  allSeen: boolean;
};

type SocialProfile = (JartinetHermandad | Banda | Organizacion) & { type: 'hermandad' | 'banda' | 'organizacion' };
function CustomMusicPlayer({ post, isHoveringContainer }: { post: SocialPost, isHoveringContainer: boolean }) {
    const [audioUrl, setAudioUrl] = useState<string | null>(null);
    // La ventana start/end solo aplica a la preview real de Spotify, no al clip de iTunes.
    const [useTimeWindow, setUseTimeWindow] = useState(false);
    const audioRef = useRef<HTMLAudioElement>(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [manualPlay, setManualPlay] = useState<boolean | null>(null);

    useEffect(() => {
        // Si se eligió la canción con el buscador, usamos su preview exacta y
        // aplicamos la ventana de segundos elegida.
        if (post.audioPreviewUrl) {
            setAudioUrl(post.audioPreviewUrl);
            setUseTimeWindow(true);
            return;
        }
        if (!post.spotifyUrl && !post.spotifyTrackName) return;
        let cancelled = false;

        const match = post.spotifyUrl?.match(/track[\/:]([a-zA-Z0-9]+)/);
        const trackId = match?.[1];

        // --- Estrategia 1: scrape del embed de Spotify (preview exacta, pero
        //     hoy Spotify la ha retirado para ~90% de las pistas) ---
        const trySpotify = async (): Promise<string | null> => {
            if (!trackId) return null;
            const embedUrl = `https://open.spotify.com/embed/track/${trackId}`;
            const proxies = [
                (u: string) => `https://api.allorigins.win/raw?url=${encodeURIComponent(u)}`,
                (u: string) => `https://corsproxy.io/?url=${encodeURIComponent(u)}`,
                (u: string) => `https://api.codetabs.com/v1/proxy/?quest=${encodeURIComponent(u)}`,
            ];
            const extractPreview = (text: string): string | null => {
                const direct = text.match(/https:\\?\/\\?\/p\.scdn\.co\\?\/mp3-preview\\?\/[A-Za-z0-9]+/);
                if (direct) return direct[0].replace(/\\\//g, '/');
                const sm = text.match(/<script id="__NEXT_DATA__"[^>]*>([\s\S]*?)<\/script>/);
                if (sm && sm[1]) {
                    try {
                        const j = JSON.parse(sm[1]);
                        const entity = j?.props?.pageProps?.state?.data?.entity;
                        return entity?.audioPreview?.url || entity?.previewUrl || null;
                    } catch { /* ignore */ }
                }
                return null;
            };
            for (const proxy of proxies) {
                try {
                    const r = await fetch(proxy(embedUrl));
                    if (!r.ok) continue;
                    const text = await r.text();
                    const preview = extractPreview(text);
                    if (preview) return preview;
                } catch { /* siguiente proxy */ }
            }
            return null;
        };

        // --- Estrategia 2: iTunes Search API (gratis, con CORS, devuelve una
        //     preview de 30s en m4a). Es la fuente fiable hoy en día. ---
        const tryItunes = async (): Promise<string | null> => {
            const term = post.spotifyTrackName?.trim();
            if (!term) return null;
            const url = `https://itunes.apple.com/search?term=${encodeURIComponent(term)}&media=music&entity=song&limit=1`;
            try {
                const r = await fetch(url);
                if (!r.ok) return null;
                const data = await r.json();
                return data?.results?.[0]?.previewUrl || null;
            } catch {
                return null;
            }
        };

        (async () => {
            // iTunes primero: es rápido y fiable. Spotify casi nunca expone ya
            // preview, y al fallar deja 3 fetches lentos antes del hover.
            const itunesPreview = await tryItunes();
            if (cancelled) return;
            if (itunesPreview) {
                setAudioUrl(itunesPreview);
                setUseTimeWindow(false);
                return;
            }
            // Último recurso: intentar la preview exacta de Spotify (con ventana).
            const spotifyPreview = await trySpotify();
            if (spotifyPreview && !cancelled) {
                setAudioUrl(spotifyPreview);
                setUseTimeWindow(true);
            }
        })();

        return () => { cancelled = true; };
    }, [post.spotifyUrl, post.spotifyTrackName, post.audioPreviewUrl]);

    useEffect(() => {
        const audio = audioRef.current;
        if (audio && audioUrl) {
            const shouldPlay = manualPlay !== null ? manualPlay : isHoveringContainer;
            if (shouldPlay) {
                audio.play().then(() => {
                    setIsPlaying(true);
                }).catch(e => {
                    console.log("Autoplay blocked:", e);
                    setIsPlaying(false);
                    if (manualPlay === null) setManualPlay(false);
                });
            } else {
                audio.pause();
                setIsPlaying(false);
            }
        }
    }, [isHoveringContainer, manualPlay, audioUrl]);

    const handleTimeUpdate = () => {
        if (!useTimeWindow) return;
        const audio = audioRef.current;
        if (audio && post.spotifyEndTime && (audio.currentTime >= post.spotifyEndTime)) {
            audio.pause();
            setIsPlaying(false);
            setManualPlay(false);
            if (post.spotifyStartTime) audio.currentTime = post.spotifyStartTime;
        }
    };

    const handleLoadedMetadata = () => {
         if (useTimeWindow && audioRef.current && post.spotifyStartTime) {
             audioRef.current.currentTime = post.spotifyStartTime;
         }
    };

    const togglePlay = (e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        if (audioUrl) {
            setManualPlay(!isPlaying);
        } else if (post.spotifyUrl) {
            window.open(post.spotifyUrl, '_blank');
        }
    };

    if (!post.spotifyUrl && !post.spotifyTrackName && !post.audioPreviewUrl) return null;

    return (
        <div className="w-full flex items-center relative" onClick={togglePlay}>
            <div className="flex items-center gap-1.5 text-primary cursor-pointer hover:opacity-80 transition-opacity">
                {isPlaying ? <Pause className="h-4 w-4 fill-current" /> : <Play className="h-4 w-4 fill-current" />}
                <span className="text-sm font-bold truncate max-w-[250px]">
                    {post.spotifyTrackName || "Audio Adjunto"}
                </span>
                {isPlaying && (
                    <div className="flex items-end gap-[2px] ml-1 h-3.5 mt-0.5">
                        <div className="w-[3px] bg-primary rounded-t-sm animate-pulse h-full" style={{ animationDuration: '0.6s' }}></div>
                        <div className="w-[3px] bg-primary rounded-t-sm animate-pulse h-[60%]" style={{ animationDuration: '0.8s' }}></div>
                        <div className="w-[3px] bg-primary rounded-t-sm animate-pulse h-[80%]" style={{ animationDuration: '0.5s' }}></div>
                    </div>
                )}
            </div>
            {audioUrl && (
                <audio 
                    ref={audioRef} 
                    src={audioUrl} 
                    onTimeUpdate={handleTimeUpdate}
                    onLoadedMetadata={handleLoadedMetadata}
                    onPause={() => setIsPlaying(false)}
                    onPlay={() => setIsPlaying(true)}
                    preload="auto"
                />
            )}
        </div>
    );
}

// --- Componentes ---
// FameHall (ranking de me gustas) se movio a src/pages/jartinet/hall-of-fame.

function CommentSheet({ post, open, onOpenChange, currentUser, onAction }: { post: SocialPost, open: boolean, onOpenChange: (open: boolean) => void, currentUser: { uid: string } | null, onAction: () => void }) {
    if (!post) return null;
    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent className="flex flex-col bg-background/80 backdrop-blur-lg">
                <SheetHeader>
                    <SheetTitle>Comentarios</SheetTitle>
                    <SheetDescription>Comentarios en la publicación de {post.authorName}.</SheetDescription>
                </SheetHeader>
                <ScrollArea className="flex-grow my-4 -mr-6 pr-6">
                    <div className="space-y-4">
                        <CommentSection post={post} currentUser={currentUser} onAction={onAction} isSheet={true} />
                    </div>
                </ScrollArea>
                <SheetFooter className="mt-auto border-t border-white/10 pt-4">
                    {currentUser && <CommentForm postId={post.id} onCommentAdded={onAction} />}
                </SheetFooter>
            </SheetContent>
        </Sheet>
    )
}

function CommentSection({ post, currentUser, onAction, isSheet = false }: { post: SocialPost, currentUser: { uid: string } | null, onAction: () => void, isSheet?: boolean }) {
    const [openSheet, setOpenSheet] = useState(false);
    const { publicMode, requireCitizen } = useSocial();

    const handleViewAll = () => {
        setOpenSheet(true);
    }

    const commentsToShow = isSheet ? post.comments : post.comments?.slice(0, 2);

    return (
        <div className="w-full pt-2">
            {post.comments && post.comments.length > 0 && (
                <div className="space-y-2 text-sm">
                    {!isSheet && post.comments.length > 2 && (
                        <button className="text-muted-foreground text-xs font-semibold" onClick={handleViewAll}>
                            Ver los {post.comments.length} comentarios
                        </button>
                    )}

                    {commentsToShow?.map((comment, index) => (
                        <CommentItem key={`${post.id}-comment-${comment.id || index}`} comment={comment} post={post} currentUser={currentUser} onAction={onAction} />
                    ))}

                </div>
            )}
            {!isSheet && currentUser && <CommentForm postId={post.id} onCommentAdded={onAction} />}
            {!isSheet && !currentUser && publicMode && (
                <button
                    onClick={() => requireCitizen()}
                    className="mt-2 w-full text-left text-sm text-muted-foreground/70 rounded-md bg-black/20 border border-white/10 px-3 h-9 flex items-center hover:border-primary/30 transition-colors"
                >
                    Añade un comentario...
                </button>
            )}
            <CommentSheet post={post} open={openSheet} onOpenChange={setOpenSheet} currentUser={currentUser} onAction={onAction} />
        </div>
    );
}

function CommentItem({ comment, post, currentUser, onAction }: { comment: SocialComment, post: SocialPost, currentUser: { uid: string } | null, onAction: () => void }) {
    const [isPending, startTransition] = useTransition();
    const { toast } = useToast();
    const [isPostAuthor, setIsPostAuthor] = useState(false);
    
    const { data: userDoc, isLoading } = useMemo(() => {
        // This should be adapted to fetch user data within your app's context
        return { data: { role: 'user'}, isLoading: false };
    }, [currentUser]);

    const isAdmin = userDoc?.role === 'admin';

    // Authorization check
    const canDelete = currentUser?.uid === comment.userId || isPostAuthor || isAdmin;

    const handleDelete = async () => {
        startTransition(async () => {
            const result = await deleteCommentFromPost(post.id, comment.id);
            if (result.success) {
                toast({ title: "Éxito", description: result.message });
                onAction();
            } else {
                toast({ title: "Error", description: result.error, variant: 'destructive' });
            }
        });
    };
    
    useEffect(() => {
        async function checkAuthor() {
            if (!currentUser || !post.authorType) return;
            // Simplified logic as server-side check is preferred
            // This is just for UI purposes and real security is in rules/backend
        }
        checkAuthor();
    }, [post, currentUser]);

    return (
        <div className="flex items-start gap-2 group">
            <Avatar className="h-6 w-6 mt-0.5">
                <AvatarImage src={comment.profileImageUrl} />
                <AvatarFallback>{comment.username.charAt(0)}</AvatarFallback>
            </Avatar>
            <div className="flex-grow">
                <p>
                    <span className="font-semibold">{comment.username}</span>:
                    {' '}
                    {comment.text}
                </p>
                 <p className="text-xs text-muted-foreground">
                    {comment.createdAt}
                </p>
            </div>
            {canDelete && (
                <AlertDialog>
                    <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-6 w-6 opacity-0 group-hover:opacity-100" disabled={isPending}>
                            <Trash2 className="h-3 w-3 text-destructive" />
                        </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                        <AlertDialogHeader>
                            <AlertDialogTitle>¿Eliminar Comentario?</AlertDialogTitle>
                            <AlertDialogDescription>
                                Esta acción es permanente y no se puede deshacer.
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                            <AlertDialogCancel>Cancelar</AlertDialogCancel>
                            <AlertDialogAction onClick={handleDelete} className="bg-destructive hover:bg-destructive/80">Eliminar</AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>
            )}
        </div>
    )
}


function CommentForm({ postId, onCommentAdded }: { postId: string, onCommentAdded: () => void }) {
    const { toast } = useToast();
    const formRef = useRef<HTMLFormElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setIsSubmitting(true);
        const formData = new FormData(event.currentTarget);
        const result = await addCommentToPost(formData);
        
        if (result.success) {
            formRef.current?.reset();
            inputRef.current?.blur();
            onCommentAdded();
            toast({ title: "Éxito", description: result.message });
        } else if (result.message) {
            toast({ title: "Error", description: result.message, variant: 'destructive' });
        }
        setIsSubmitting(false);
    };

    return (
        <form onSubmit={handleSubmit} ref={formRef} className="flex items-center gap-2 pt-2">
            <input type="hidden" name="postId" value={postId} />
            <Input
                ref={inputRef}
                name="commentText"
                placeholder="Añade un comentario..."
                className="flex-grow h-9 bg-black/20 border-white/20 placeholder:text-white/40"
                autoComplete="off"
            />
            <Button type="submit" size="icon" className="h-9 w-9 flex-shrink-0" disabled={isSubmitting}>
                {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin"/> : <Send className="h-4 w-4" />}
            </Button>
        </form>
    );
}

function PostCard({ post, onHeaderClick, currentUser, onAction, onShareClick }: { post: SocialPost, onHeaderClick: (type: 'hermandad' | 'banda' | 'organizacion', id: string) => void, currentUser: { uid: string } | null, onAction: () => void, onShareClick: (post: SocialPost) => void; }) {
    const videoRef = useRef<HTMLVideoElement>(null);
    const [isPlaying, setIsPlaying] = useState(true);
    const [isMuted, setIsMuted] = useState(true);
    const [isHovering, setIsHovering] = useState(false);
    const [isHoveringCard, setIsHoveringCard] = useState(false);
    const [isPending, startTransition] = useTransition();
    const [volume, setVolume] = useState(0); 
    const [optimisticLikes, setOptimisticLikes] = useState(post.likes || []);
    const [aspectRatio, setAspectRatio] = useState<number>(1);
    const [api, setApi] = useState<CarouselApi>();
    const [currentSlide, setCurrentSlide] = useState(0);
    
    const mediaUrls = Array.isArray(post.mediaUrl) ? post.mediaUrl : (post.mediaUrl ? [post.mediaUrl] : []);
    const hasMedia = mediaUrls.length > 0;
    const isMultiMedia = mediaUrls.length > 1;
    const isCollaboration = post.collaboratorId && post.collaborationStatus === 'accepted';
    
    const userHasLiked = currentUser ? optimisticLikes.includes(currentUser.uid) : false;
    const { requireCitizen } = useSocial();
    const hasMusic = !!(post.spotifyUrl || post.audioPreviewUrl || post.spotifyTrackName);

    useEffect(() => {
        if (!api) return;
        
        const onSelect = () => {
            setCurrentSlide(api.selectedScrollSnap());
        };
        api.on("select", onSelect);
        return () => { api.off("select", onSelect) };
    }, [api]);

    useEffect(() => {
        if (hasMedia) {
             const img = new window.Image();
             img.src = mediaUrls[0];
             img.onload = () => {
                 if(img.naturalHeight > 0) {
                     setAspectRatio(img.naturalWidth / img.naturalHeight);
                 }
             }
        }
    }, [hasMedia, mediaUrls]);


    useEffect(() => {
        if (videoRef.current) {
            videoRef.current.volume = volume;
            videoRef.current.muted = isMuted;
        }
    }, [volume, isMuted]);

    const handleLikeClick = async () => {
      if (!requireCitizen()) return;
      if (!currentUser) return;
      const newLikes = userHasLiked ? optimisticLikes.filter(id => id !== currentUser.uid) : [...optimisticLikes, currentUser.uid];
      setOptimisticLikes(newLikes);
      await toggleLikePost(post.id);
    };

    const togglePlay = () => {
        if (videoRef.current) {
            if (videoRef.current.paused) {
                videoRef.current.play();
                setIsPlaying(true);
            } else {
                videoRef.current.pause();
                setIsPlaying(false);
            }
        }
    };
    
    const toggleMute = (e: React.MouseEvent) => {
        e.stopPropagation(); 
        const newMutedState = !isMuted;
        setIsMuted(newMutedState);
        if (videoRef.current) {
            videoRef.current.muted = newMutedState;
            if(!newMutedState && volume === 0) setVolume(0.5);
            if(newMutedState) setVolume(0);
        }
    };
    
    const handleVolumeChange = (value: number[]) => {
      const newVolume = value[0];
      setVolume(newVolume);
      setIsMuted(newVolume === 0);
    };

    const renderMediaItem = (url: string) => {
        const isVideo = url.endsWith('.mp4') || url.endsWith('.mov');
        
        if (isVideo) {
             return (
                <video
                    ref={videoRef}
                    src={url}
                    className="w-full h-full object-cover"
                    autoPlay
                    loop
                    playsInline
                    muted={isMuted}
                    onClick={togglePlay}
                />
            );
        }

        // Tarjeta del feed: 3 por fila, o sea ~350-450 px. Va la miniatura; el
        // original se sirve al abrir el post en `PostViewerDialog`. [165]
        return (
             <Image
                thumb
                src={url}
                alt={`Contenido para ${post.title}`}
                className="object-contain w-full h-full"
            />
        );
    }
    
    const currentSlideUrl = mediaUrls[currentSlide];
    const isCurrentSlideVideo = currentSlideUrl?.endsWith('.mp4') || currentSlideUrl?.endsWith('.mov');


    return (
        <div
            className="group relative bg-card/40 backdrop-blur-sm border border-white/8 rounded-2xl overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:border-primary/30 hover:shadow-[0_12px_50px_rgba(0,0,0,0.5)] flex flex-col h-full"
            onMouseEnter={() => setIsHoveringCard(true)}
            onMouseLeave={() => setIsHoveringCard(false)}
        >
            {/* ── Header ── */}
            <div className="flex items-center gap-3 px-4 pt-4 pb-3">
                <div className="flex -space-x-3 flex-shrink-0">
                    <Avatar className="h-9 w-9 border-2 border-background/80 cursor-pointer ring-2 ring-transparent group-hover:ring-primary/20 transition-all" onClick={() => onHeaderClick(post.authorType, post.authorId)}>
                        <AvatarImage src={post.authorShieldUrl} alt={post.authorName} />
                        <AvatarFallback>{post.authorName?.substring(0, 2)}</AvatarFallback>
                    </Avatar>
                    {isCollaboration && post.collaboratorId && (
                        <Avatar className="h-9 w-9 border-2 border-background/80 cursor-pointer" onClick={() => onHeaderClick(post.collaboratorId!.split(':')[0] as 'hermandad' | 'banda' | 'organizacion', post.collaboratorId!.split(':')[1])}>
                            <AvatarImage src={post.collaboratorShieldUrl} alt={post.collaboratorName} />
                            <AvatarFallback>{post.collaboratorName?.substring(0, 2)}</AvatarFallback>
                        </Avatar>
                    )}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                        <button onClick={() => onHeaderClick(post.authorType, post.authorId)} className="font-semibold text-sm hover:text-primary transition-colors truncate">
                            {post.authorName}
                        </button>
                        {isCollaboration && post.collaboratorId && (
                            <>
                                <LinkIcon className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                                <button onClick={() => onHeaderClick(post.collaboratorId!.split(':')[0] as 'hermandad' | 'banda' | 'organizacion', post.collaboratorId!.split(':')[1])} className="font-semibold text-sm hover:text-primary transition-colors truncate">
                                    {post.collaboratorName}
                                </button>
                            </>
                        )}
                    </div>
                    <p className="text-[0.68rem] text-muted-foreground/70 leading-none mt-0.5">{post.createdAt as string}</p>
                </div>
            </div>

            {/* ── Música (debajo del perfil, sin caja) ── */}
            {hasMusic && (
                <div className="px-4 pb-3 -mt-1">
                    <CustomMusicPlayer post={post} isHoveringContainer={isHoveringCard} />
                </div>
            )}

            {/* ── Media ── */}
            {hasMedia && (
                <div
                    className="relative w-full bg-black/30 overflow-hidden"
                    style={{ aspectRatio: `${Math.max(0.5, Math.min(aspectRatio, 2))}` }}
                    onMouseEnter={() => setIsHovering(true)}
                    onMouseLeave={() => setIsHovering(false)}
                >
                    {isMultiMedia ? (
                        <Carousel className="w-full h-full" setApi={setApi}>
                            <CarouselContent className="-ml-0 h-full">
                                {mediaUrls.map((url, index) => (
                                    <CarouselItem key={index} className="pl-0 h-full flex items-center justify-center">
                                        <div className="relative w-full h-full flex items-center justify-center">
                                            {renderMediaItem(url)}
                                        </div>
                                    </CarouselItem>
                                ))}
                            </CarouselContent>
                            <CarouselPrevious className="absolute left-2 top-1/2 -translate-y-1/2 h-8 w-8" />
                            <CarouselNext className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8" />
                            <div className="absolute top-2.5 right-2.5 flex items-center justify-center h-5 rounded-full bg-black/60 text-white text-[0.65rem] font-bold px-2 backdrop-blur-sm">
                                <Copy className="h-2.5 w-2.5 mr-1" /> {currentSlide + 1}/{mediaUrls.length}
                            </div>
                        </Carousel>
                    ) : (
                        <div className="relative w-full h-full flex items-center justify-center">
                            {renderMediaItem(mediaUrls[0])}
                            {post.mediaType === 'video' && isHovering && (
                                <div className="absolute bottom-2.5 right-2.5 flex items-center gap-1.5 group/volume">
                                    <Button size="icon" variant="ghost" onClick={togglePlay} className="text-white bg-black/40 hover:bg-black/60 h-8 w-8 backdrop-blur-sm rounded-full">
                                        {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                                    </Button>
                                    <div className="flex items-center gap-1.5">
                                        <Button size="icon" variant="ghost" onClick={toggleMute} className="text-white bg-black/40 hover:bg-black/60 h-8 w-8 backdrop-blur-sm rounded-full">
                                            {isMuted || volume === 0 ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
                                        </Button>
                                        <div className="w-0 group-hover/volume:w-20 overflow-hidden transition-[width] duration-300">
                                            <Slider value={[volume]} onValueChange={handleVolumeChange} max={1} step={0.1} />
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* ── Body ── */}
            <div className="px-4 py-3 flex-1">
                <p className="font-bold text-sm leading-snug mb-1">{post.title}</p>
                <SocialText content={post.content} className="text-muted-foreground" />
            </div>

            {/* ── Footer ── */}
            <div className="px-4 pb-4 pt-2 border-t border-white/5 space-y-2">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-0.5">
                        <button
                            onClick={handleLikeClick}
                            disabled={isPending}
                            className={cn(
                                "flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl text-sm font-medium transition-all duration-200",
                                userHasLiked
                                    ? "text-red-500 bg-red-500/10"
                                    : "text-muted-foreground hover:text-red-500 hover:bg-red-500/10"
                            )}
                        >
                            <Heart className={cn("h-4.5 w-4.5", userHasLiked && "fill-current")} />
                            <span className="text-xs">{optimisticLikes.length}</span>
                        </button>
                        <button className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl text-muted-foreground hover:text-primary hover:bg-primary/10 transition-all duration-200 text-sm">
                            <MessageCircle className="h-4 w-4" />
                            <span className="text-xs">{post.comments?.length || 0}</span>
                        </button>
                        <button
                            onClick={() => { if (!requireCitizen()) return; onShareClick(post); }}
                            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl text-muted-foreground hover:text-primary hover:bg-primary/10 transition-all duration-200"
                        >
                            <Send className="h-4 w-4" />
                        </button>
                    </div>
                </div>
                <CommentSection post={post} currentUser={currentUser} onAction={onAction} />
            </div>
        </div>
    );
}

function PostViewerDialog({ post, open, onOpenChange, currentUser, onAction, onShareClick }: { post: SocialPost | null, open: boolean, onOpenChange: (open: boolean) => void, currentUser: {uid: string} | null, onAction: () => void, onShareClick: (post: SocialPost) => void; }) {
    if (!post) return null;

    const mediaUrls = Array.isArray(post.mediaUrl) ? post.mediaUrl : (post.mediaUrl ? [post.mediaUrl] : []);
    const hasMedia = mediaUrls.length > 0;
    const isMultiMedia = mediaUrls.length > 1;

    const [isHovering, setIsHovering] = useState(false);
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent 
                className="max-w-4xl w-full p-0 gap-0 grid grid-cols-1 md:grid-cols-2 h-[90vh] bg-background/80 backdrop-blur-lg border-white/10"
                onMouseEnter={() => setIsHovering(true)}
                onMouseLeave={() => setIsHovering(false)}
            >
                {hasMedia && (
                    <div className="relative bg-black aspect-square md:aspect-auto">
                        {isMultiMedia ? (
                            <Carousel className="w-full h-full">
                                <CarouselContent className="h-full">
                                    {mediaUrls.map((url, index) => (
                                        <CarouselItem key={index} className="relative h-full flex items-center justify-center">
                                            <Image 
                                                src={url} 
                                                alt={`Contenido para ${post.title} - ${index + 1}`} 
                                                className="object-contain w-full h-full"
                                            />
                                        </CarouselItem>
                                    ))}
                                </CarouselContent>
                                <CarouselPrevious className="absolute left-2 top-1/2 -translate-y-1/2" />
                                <CarouselNext className="absolute right-2 top-1/2 -translate-y-1/2" />
                            </Carousel>
                        ) : post.mediaType === 'video' ? (
                            <video src={mediaUrls[0]} className="w-full h-full object-contain" controls autoPlay playsInline />
                        ) : (
                            <div className="relative w-full h-full flex items-center justify-center">
                               <Image src={mediaUrls[0]} alt={`Contenido para ${post.title}`} className="object-contain w-full h-full" />
                            </div>
                        )}
                    </div>
                )}
                <div className={cn("flex flex-col", !hasMedia && "md:col-span-2")}>
                    <DialogHeader className="p-4 border-b border-white/10">
                         <div className="flex items-center gap-3 text-left">
                            <Avatar className="h-10 w-10 border">
                                <AvatarImage src={post.authorShieldUrl} alt={`Escudo de ${post.authorName}`} />
                                <AvatarFallback>{post.authorName?.substring(0, 2)}</AvatarFallback>
                            </Avatar>
                            <div>
                                <p className="font-semibold">{post.authorName}</p>
                                <p className="text-xs text-muted-foreground">{post.createdAt as string}</p>
                            </div>
                        </div>
                    </DialogHeader>
                    <ScrollArea className="flex-grow p-4">
                        <h3 className="font-bold text-lg mb-2">{post.title}</h3>
                        <SocialText content={post.content} className="text-muted-foreground" />
                        {(post.spotifyUrl || post.audioPreviewUrl || post.spotifyTrackName) && <CustomMusicPlayer post={post} isHoveringContainer={isHovering} />}
                        <div className="mt-4">
                          <CommentSection post={post} currentUser={currentUser} onAction={onAction} isSheet={true} />
                        </div>
                    </ScrollArea>
                </div>
            </DialogContent>
        </Dialog>
    );
}

function SharePostDialog({ post, open, onOpenChange, userProfiles, onAction }: { post: SocialPost | null, open: boolean, onOpenChange: (open: boolean) => void, userProfiles: { hermandades: JartinetHermandad[], bandas: Banda[], organizaciones: Organizacion[] }, onAction: () => void }) {
    const { toast } = useToast();
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!post) return;
        setIsSubmitting(true);
        const formData = new FormData(event.currentTarget);
        const result = await sharePostToStory(formData);
        
        if (result?.success) {
            toast({ title: 'Éxito', description: result.message });
            onOpenChange(false);
            onAction();
        } else {
            toast({ title: 'Error', description: result?.message || 'No se pudo compartir la publicación.', variant: 'destructive' });
        }
        setIsSubmitting(false);
    };
    
    if (!post) return null;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="bg-background/80 backdrop-blur-lg border-white/10">
                <DialogHeader>
                    <DialogTitle>Compartir en tu historia</DialogTitle>
                    <DialogDescription>
                        Elige en nombre de qué perfil quieres compartir esta publicación.
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <input type="hidden" name="postId" value={post.id} />
                    <div className="space-y-2">
                        <Label htmlFor="authorId">Compartir como:</Label>
                        <Select name="authorId" required>
                            <SelectTrigger>
                                <SelectValue placeholder="Selecciona un perfil..." />
                            </SelectTrigger>
                            <SelectContent>
                                {userProfiles.hermandades.map(h => (
                                    <SelectItem key={`hermandad:${h.id}`} value={`hermandad:${h.id}`}>
                                        <div className="flex items-center gap-2">
                                            <Avatar className="h-5 w-5"><AvatarImage src={h.escudoUrl} /></Avatar>
                                            {h.nombreCompleto} (Hermandad)
                                        </div>
                                    </SelectItem>
                                ))}
                                {userProfiles.bandas.map(b => (
                                    <SelectItem key={`banda:${b.id}`} value={`banda:${b.id}`}>
                                        <div className="flex items-center gap-2">
                                            <Avatar className="h-5 w-5"><AvatarImage src={b.escudoUrl} /></Avatar>
                                            {b.nombreCompleto} (Banda)
                                        </div>
                                    </SelectItem>
                                ))}
                                {userProfiles.organizaciones.map(o => (
                                    <SelectItem key={`organizacion:${o.id}`} value={`organizacion:${o.id}`}>
                                        <div className="flex items-center gap-2">
                                            <Avatar className="h-5 w-5"><AvatarImage src={o.escudoUrl} /></Avatar>
                                            {o.nombreCompleto} (Organización)
                                        </div>
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <DialogFooter>
                        <DialogClose asChild><Button variant="ghost">Cancelar</Button></DialogClose>
                        <Button type="submit" disabled={isSubmitting}>
                            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin"/>}
                            Compartir
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

function ProfileSheet({ 
    profile, 
    allPosts, 
    stories,
    pendingCollaborations,
    onOpenChange, 
    open, 
    onStoryClick,
    onHighlightClick,
    onPostClick,
    currentUser,
    onAction,
}: { 
    profile: SocialProfile | null;
    allPosts: SocialPost[]; 
    stories: StoryGroup | undefined;
    pendingCollaborations: SocialPost[];
    open: boolean; 
    onOpenChange: (open: boolean) => void; 
    onStoryClick: () => void;
    onHighlightClick: (group: HighlightStoryGroup) => void;
    onPostClick: (post: SocialPost) => void;
    currentUser: { uid: string } | null;
    onAction: () => void;
}) {
  if (!profile) return null;

  const [activeTab, setActiveTab] = useState<'posts' | 'collaborations'>('posts');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleCollaborationAction = async (action: 'accept' | 'reject', postId: string) => {
    setIsSubmitting(true);
    const actionFunc = action === 'accept' ? acceptCollaboration : rejectCollaboration;
    const result = await actionFunc(postId);
    if (result.success) {
      toast({ title: 'Éxito', description: result.message });
      onAction(); // Recargar datos
    } else {
      toast({ title: 'Error', description: result.error, variant: 'destructive' });
    }
    setIsSubmitting(false);
  };
  
  const getManagerUids = () => {
    switch (profile.type) {
        case 'hermandad': return (profile as JartinetHermandad).hermanosMayores;
        case 'banda': return (profile as Banda).directores;
        case 'organizacion': return (profile as Organizacion).gestores;
        default: return [];
    }
  }

  const isManager = currentUser ? (() => { const m = getManagerUids(); return Array.isArray(m) && m.includes(currentUser.uid); })() : false;

  const pinnedPostIds = useMemo(() => {
    if (!profile?.pinnedPosts) return new Set<string>();
    return new Set(Object.values(profile.pinnedPosts).filter(Boolean) as string[]);
  }, [profile]);


  const profilePosts = useMemo(() => {
    if (!profile) return [];
    const profileKey = `${profile.type}:${profile.id}`;
    return allPosts.filter(p => {
        const isAuthor = p.authorId === profile.id && p.authorType === profile.type;
        const isCollaborator = p.collaboratorId === profileKey && p.collaborationStatus === 'accepted';
        const isPinned = pinnedPostIds.has(p.id);
        return (isAuthor || isCollaborator) && !isPinned;
    });
  }, [allPosts, profile, pinnedPostIds]);
  
  const totalBrothers = profile.type === 'hermandad' ? ((profile as JartinetHermandad).hermanos?.length || 0) + ((profile as JartinetHermandad).hermanosMayores?.length || 0) : 0;
  const totalLikes = useMemo(() => profilePosts.reduce((acc, post) => acc + (post.likes?.length || 0), 0), [profilePosts]);
  
  const highlightGroups: HighlightStoryGroup[] = profile.highlightGroups || [];


  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-xl w-full flex flex-col p-0 bg-background/80 backdrop-blur-lg border-white/10 text-white">
        {profile && (
          <SheetHeader 
            className="p-6 pb-4 border-b-4"
            style={{ 
              backgroundColor: profile.colorPrimario,
              borderColor: profile.colorSecundario,
            }}
          >
            <div className="flex items-center gap-4">
              <div className="flex-shrink-0">
                  {stories && stories.stories.length > 0 ? (
                      <StoryCircle storyGroup={stories} onClick={onStoryClick} size="h-20 w-20" />
                  ) : (
                      <Avatar className="h-20 w-20 border-2">
                          <AvatarImage src={profile.escudoUrl} />
                          <AvatarFallback>{(profile.nombreCompleto || (profile as JartinetHermandad).nombre).substring(0, 2)}</AvatarFallback>
                      </Avatar>
                  )}
              </div>
              <div className="flex-grow">
                <SheetTitle className="text-2xl text-white">{profile.nombreCompleto || (profile as JartinetHermandad).nombre}</SheetTitle>
                {profile.type === 'hermandad' && <SheetDescription className="text-white/80">{(profile as JartinetHermandad).nombre}</SheetDescription>}
                {profile.type === 'banda' && <SheetDescription className="text-white/80">{(profile as Banda).tipo}</SheetDescription>}
                {profile.type === 'organizacion' && <SheetDescription className="text-white/80">Organización</SheetDescription>}
              </div>
            </div>
            <div className="flex justify-around pt-4 text-center">
              <div>
                <p className="font-bold text-lg">{profilePosts.length + pinnedPostIds.size}</p>
                <p className="text-sm text-white/80">Publicaciones</p>
              </div>
              {profile.type === 'hermandad' && (
               <div>
                <p className="font-bold text-lg">{totalBrothers}</p>
                <p className="text-sm text-white/80">Hermanos</p>
              </div>
              )}
              <div>
                <p className="font-bold text-lg">{totalLikes}</p>
                <p className="text-sm text-white/80">Me gustas</p>
              </div>
            </div>
            {highlightGroups.length > 0 && (
                <div className="pt-4">
                  <ScrollArea>
                    <div className="flex gap-4 pb-2">
                      {highlightGroups.map(group => (
                        <div key={group.id} className="flex flex-col items-center gap-1.5 w-20 flex-shrink-0">
                          <button className="h-20 w-20 rounded-full p-1 bg-white/30" onClick={() => onHighlightClick(group)}>
                             <div className="bg-background/80 p-0.5 rounded-full h-full w-full">
                                <Avatar className="w-full h-full">
                                  <AvatarImage src={group.coverUrl} />
                                </Avatar>
                              </div>
                          </button>
                          <p className="text-xs text-center font-medium truncate w-full text-white/90">{group.name}</p>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </div>
            )}
          </SheetHeader>
        )}

        {isManager && (
          <div className="flex gap-1 border-b border-white/10 p-1">
            <Button variant={activeTab === 'posts' ? 'secondary' : 'ghost'} onClick={() => setActiveTab('posts')} className="flex-1">Publicaciones</Button>
            <Button variant={activeTab === 'collaborations' ? 'secondary' : 'ghost'} onClick={() => setActiveTab('collaborations')} className="flex-1 relative">
              Colaboraciones
              {pendingCollaborations.length > 0 && (
                <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-xs text-primary-foreground">
                  {pendingCollaborations.length}
                </span>
              )}
            </Button>
          </div>
        )}

        <ScrollArea className="flex-grow relative">
          <div
              className="absolute inset-0 opacity-[0.03]"
              style={{
                backgroundImage: `url(${profile.escudoUrl})`,
                backgroundSize: '120px',
                backgroundRepeat: 'repeat',
              }}
            />
          <div className="relative z-10">
            {profile.pinnedPosts && Object.values(profile.pinnedPosts).some(p => p) && (
              <div className="p-2">
                  <h3 className="font-semibold text-sm mb-2 pl-2">Publicaciones Fijadas</h3>
                  <div className="grid grid-cols-3 gap-0.5">
                      {Object.values(profile.pinnedPosts).map(postId => {
                          const post = allPosts.find(p => p.id === postId);
                          if (!post) return null;
                          const mediaUrl = Array.isArray(post.mediaUrl) ? post.mediaUrl[0] : post.mediaUrl;
                          return (
                              <button key={post.id} className="relative aspect-square group bg-black/30" onClick={() => onPostClick(post)}>
                                  {mediaUrl ? <Image thumb src={mediaUrl} alt={post.title} fill className="object-cover" /> : <div className="w-full h-full flex items-center justify-center p-2"><span className="text-xs text-center line-clamp-2">{post.title}</span></div> }
                                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white p-1 text-center">
                                    <span className="text-xs font-bold line-clamp-2">{post.title}</span>
                                  </div>
                              </button>
                          )
                      })}
                  </div>
              </div>
            )}
          {activeTab === 'posts' && (
            <div className="grid grid-cols-3 gap-0.5 p-0.5">
                {profilePosts.map(post => {
                  const mediaUrls = Array.isArray(post.mediaUrl) ? post.mediaUrl : (post.mediaUrl ? [post.mediaUrl] : []);
                  const hasMedia = mediaUrls.length > 0;
                  const isMultiMedia = mediaUrls.length > 1;

                  return (
                    <button key={post.id} className="relative aspect-square group bg-black/30" onClick={() => onPostClick(post)}>
                        {hasMedia ? (
                            post.mediaType === 'video' ? (
                                <div className="w-full h-full flex items-center justify-center">
                                    <video src={mediaUrls[0]} className="max-w-full max-h-full object-cover w-full h-full" />
                                    <div className="absolute inset-0 bg-black/20"></div>
                                    <Play className="absolute text-white h-8 w-8 drop-shadow-md" fill="white" />
                                </div>
                            ) : (
                                <Image thumb src={mediaUrls[0]} alt={post.title} fill className="object-cover" />
                            )
                        ) : (
                            <div className="w-full h-full flex items-center justify-center p-4">
                                <Image src={post.authorShieldUrl} alt={post.authorName} width={64} height={64} className="object-contain opacity-50" />
                            </div>
                        )}
                         {isMultiMedia && <Copy className="absolute top-1.5 right-1.5 h-4 w-4 text-white drop-shadow-md" />}
                         <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white p-2 text-center">
                           <span className="text-xs font-bold line-clamp-2">{post.title}</span>
                        </div>
                    </button>
                )})}
            </div>
          )}
          {activeTab === 'collaborations' && isManager && (
            <div className="space-y-4 p-4">
              <h3 className="font-semibold text-lg flex items-center gap-2"><Handshake className="h-5 w-5" /> Invitaciones Pendientes ({pendingCollaborations.length})</h3>
              {pendingCollaborations.length > 0 ? (
                pendingCollaborations.map(post => (
                  <div key={post.id} className="flex items-center justify-between gap-3 text-sm p-3 rounded-md bg-black/20 border border-white/10">
                    <div className="flex items-center gap-3 flex-grow overflow-hidden">
                      <Avatar className="h-10 w-10">
                        <AvatarImage src={post.authorShieldUrl} />
                        <AvatarFallback>{post.authorName?.substring(0, 2)}</AvatarFallback>
                      </Avatar>
                      <div className='truncate'>
                        <p className="font-semibold truncate">{post.authorName}</p>
                        <p className="text-xs text-white/70 truncate">Te ha invitado a colaborar en: "{post.title}"</p>
                      </div>
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => handleCollaborationAction('reject', post.id)} disabled={isSubmitting}><X className="h-4 w-4" /></Button>
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-green-500" onClick={() => handleCollaborationAction('accept', post.id)} disabled={isSubmitting}><Check className="h-4 w-4" /></Button>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-white/70 text-sm py-8 text-center">No tienes invitaciones de colaboración pendientes.</p>
              )}
            </div>
          )}
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  )
}

function StoryCircle({ storyGroup, onClick, size = 'h-16 w-16' }: { storyGroup: StoryGroup, onClick: () => void, size?: string }) {
    return (
        <div className="flex flex-col items-center gap-2 flex-shrink-0">
             <button onClick={onClick} className="focus:outline-none transition-transform duration-200 hover:scale-105 active:scale-95">
                <div className={`rounded-full p-[2.5px] ${storyGroup.allSeen ? 'bg-white/25' : 'bg-gradient-to-tr from-primary via-fuchsia-500 to-amber-400 shadow-[0_0_14px_rgba(168,85,247,0.45)]'}`}>
                    <div className="bg-black/80 p-0.5 rounded-full">
                        <Avatar className={`${size} border-2 border-black/80`}>
                            <AvatarImage src={storyGroup.shield} alt={`Escudo de ${storyGroup.name}`} />
                            <AvatarFallback>{storyGroup.name.substring(0, 2)}</AvatarFallback>
                        </Avatar>
                    </div>
                </div>
            </button>
            {!size.includes('h-20') && <p className="text-xs text-center font-medium truncate w-20 text-white">{storyGroup.name}</p>}
        </div>
    );
}

interface SocialPageClientProps {
    initialHermandades: JartinetHermandad[];
    initialBandas: Banda[];
    initialOrganizaciones: Organizacion[];
    initialPosts: SocialPost[];
    initialStories: Record<string, SocialStory[]>;
    initialUserProfiles: Record<string, UserProfile>;
    allPosts: SocialPost[];
    currentUser: { uid: string } | null;
    publicMode?: boolean;
}

export function SocialPageClient({
    initialHermandades,
    initialBandas,
    initialOrganizaciones,
    initialPosts,
    initialStories,
    initialUserProfiles,
    allPosts,
    currentUser,
    publicMode = false,
}: SocialPageClientProps) {
  const { toast } = useToast();
  const [citizenDialogOpen, setCitizenDialogOpen] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [activeStoryGroupIndex, setActiveStoryGroupIndex] = useState(0);

  const [profileSheetOpen, setProfileSheetOpen] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState<SocialProfile | null>(null);
  
  const [postViewerOpen, setPostViewerOpen] = useState(false);
  const [selectedPost, setSelectedPost] = useState<SocialPost | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearchResults, setShowSearchResults] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [postToShare, setPostToShare] = useState<SocialPost | null>(null);
  
  const [isHighlightViewerOpen, setIsHighlightViewerOpen] = useState(false);
  const [activeHighlightGroup, setActiveHighlightGroup] = useState<HighlightStoryGroup | null>(null);


  const handleActionCompletion = () => {
    window.location.reload();
  };

  const requireCitizen = useCallback(() => {
    if (publicMode) {
        setCitizenDialogOpen(true);
        return false;
    }
    return true;
  }, [publicMode]);

  const socialProfiles = useMemo(() => {
    const hermandadesWithMeta = initialHermandades.map(h => ({ ...h, type: 'hermandad' as const, storyId: `hermandad:${h.id}`}));
    const bandasWithMeta = initialBandas.map(b => ({ ...b, type: 'banda' as const, storyId: `banda:${b.id}`}));
    const organizacionesWithMeta = initialOrganizaciones.map(o => ({ ...o, type: 'organizacion' as const, storyId: `organizacion:${o.id}`}));
    return [...hermandadesWithMeta, ...bandasWithMeta, ...organizacionesWithMeta];
  }, [initialHermandades, initialBandas, initialOrganizaciones]);

  const profileMap = useMemo(() => {
    const map = new Map<string, SocialProfile>();
    socialProfiles.forEach(p => map.set(`${p.type}:${p.id}`, p));
    return map;
  }, [socialProfiles]);

  const mentionProfiles = useMemo(() => {
    const map = new Map<string, MentionProfile>();
    socialProfiles.forEach(p => map.set(`${p.type}:${p.id}`, {
        type: p.type,
        id: p.id,
        name: p.type === 'hermandad' ? (p as JartinetHermandad).nombre : p.nombreCompleto,
        escudoUrl: p.escudoUrl,
    }));
    return map;
  }, [socialProfiles]);

  const stories: StoryGroup[] = useMemo(() => {
    if (!initialStories || !socialProfiles.length) return [];
  
    const now = new Date();
  
    const activeStories = Object.values(initialStories).flat().filter(story => {
      try {
        const expiresAtDate = story.expiresAt ? new Date(story.expiresAt) : new Date(0);
        return !isNaN(expiresAtDate.getTime()) && expiresAtDate > now;
      } catch (e) {
        return false;
      }
    });

    const storiesByAuthorId = activeStories.reduce((acc, story) => {
        const authorKey = `${story.authorType}:${story.authorId}`;
        if (!acc[authorKey]) acc[authorKey] = [];
        acc[authorKey].push(story);
        return acc;
    }, {} as Record<string, SocialStory[]>);
  
    const allGroups = socialProfiles.map(profile => {
        const profileKey = `${profile.type}:${profile.id}`;
        const profileStories = storiesByAuthorId[profileKey] || [];
        if (profileStories.length === 0) return null;

        const allSeen = currentUser ? profileStories.every(s => s.viewedBy?.includes(currentUser.uid)) : true;

        return {
            id: profileKey,
            type: profile.type,
            name: profile.type === 'hermandad' ? (profile as JartinetHermandad).nombre : profile.nombreCompleto,
            shield: profile.escudoUrl,
            stories: profileStories.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()),
            allSeen,
        };
    }).filter((g): g is StoryGroup => g !== null);

    return allGroups.sort((a, b) => {
        if (a.allSeen !== b.allSeen) return a.allSeen ? 1 : -1;
        const lastStoryTimeA = a.stories.length > 0 ? new Date(a.stories[a.stories.length - 1].createdAt).getTime() : 0;
        const lastStoryTimeB = b.stories.length > 0 ? new Date(b.stories[b.stories.length - 1].createdAt).getTime() : 0;
        return lastStoryTimeB - lastStoryTimeA;
    });
  }, [initialStories, currentUser, socialProfiles]);


  const filteredProfiles = useMemo(() => {
    if (!searchQuery) return [];
    const lowercasedQuery = searchQuery.toLowerCase();
    return socialProfiles.filter(profile => 
        (profile.nombreCompleto || (profile as JartinetHermandad).nombre).toLowerCase().includes(lowercasedQuery)
    );
  }, [socialProfiles, searchQuery]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowSearchResults(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleStoryClick = (group: StoryGroup) => {
    const originalIndex = stories.findIndex(sg => sg.id === group.id);
    if(originalIndex !== -1) {
      setActiveStoryGroupIndex(originalIndex);
      setViewerOpen(true);
    }
  }

  const handleProfileClick = (type: 'hermandad' | 'banda' | 'organizacion', id: string) => {
    const profile = profileMap.get(`${type}:${id}`);
    if (profile) {
        setSelectedProfile(profile);
        setProfileSheetOpen(true);
        setShowSearchResults(false);
        setSearchQuery('');
    }
  }

  const handlePostClickFromProfile = (post: SocialPost) => {
      setSelectedPost(post);
      setPostViewerOpen(true);
  }
  
  const handleStoryClickFromProfile = () => {
    if (!selectedProfile) return;
    const storyGroupIndex = stories.findIndex(sg => sg.id === `${selectedProfile.type}:${selectedProfile.id}`);
    if (storyGroupIndex !== -1 && stories[storyGroupIndex].stories.length > 0) {
        setActiveStoryGroupIndex(storyGroupIndex);
        setProfileSheetOpen(false);
        setTimeout(() => setViewerOpen(true), 150);
    } else {
        toast({
            title: "Sin Historias",
            description: "Este perfil no tiene historias activas.",
        });
    }
  }
  
  const handleHighlightClick = (group: HighlightStoryGroup) => {
    setActiveHighlightGroup(group);
    setIsHighlightViewerOpen(true);
  };

  const handleShareClick = (post: SocialPost) => {
    setPostToShare(post);
    setShareDialogOpen(true);
  };

  const userManagedProfiles = useMemo(() => {
    if (!currentUser) return { hermandades: [], bandas: [], organizaciones: [] };
    const managedHermandades = initialHermandades.filter(h => Array.isArray(h.hermanosMayores) && h.hermanosMayores.includes(currentUser.uid));
    const managedBandas = initialBandas.filter(b => Array.isArray(b.directores) && b.directores.includes(currentUser.uid));
    const managedOrganizaciones = initialOrganizaciones.filter(o => Array.isArray(o.gestores) && o.gestores.includes(currentUser.uid));
    return { hermandades: managedHermandades, bandas: managedBandas, organizaciones: managedOrganizaciones };
  }, [currentUser, initialHermandades, initialBandas, initialOrganizaciones]);
  
  const pendingCollaborations = useMemo(() => {
    if (!selectedProfile || !allPosts) return [];
    const profileKey = `${selectedProfile.type}:${selectedProfile.id}`;
    return allPosts.filter(p => p.collaboratorId === profileKey && p.collaborationStatus === 'pending');
  }, [allPosts, selectedProfile]);

  const isLoading = !initialPosts || !initialStories || !initialHermandades || !initialOrganizaciones;

  return (
   <SocialContext.Provider value={{ publicMode, requireCitizen, profiles: mentionProfiles, onMention: handleProfileClick }}>
    <div className="space-y-6">
      {publicMode && (
        <div className="flex items-center justify-center gap-2 rounded-2xl border border-primary/20 bg-primary/10 px-4 py-2.5 text-center text-sm text-primary">
            <Lock className="h-4 w-4 flex-shrink-0" />
            <span>Estás viendo el escaparate público. Para interactuar debes ser <strong>ciudadano de Jartiland</strong>.</span>
        </div>
      )}
      {/* ── Hero ── */}
      <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-primary/25 via-purple-600/10 to-card/40 p-6 sm:p-8">
          <div className="absolute -top-20 -right-16 h-64 w-64 rounded-full bg-primary/30 blur-[90px] pointer-events-none" />
          <div className="absolute -bottom-24 -left-10 h-56 w-56 rounded-full bg-purple-500/20 blur-[90px] pointer-events-none" />
          <div
              className="absolute inset-0 opacity-[0.04] pointer-events-none"
              style={{ backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.6) 1px, transparent 1px)', backgroundSize: '22px 22px' }}
          />
          <div className="relative flex flex-col gap-1.5">
              <div className="flex items-center gap-2 text-primary">
                  <Sparkles className="h-5 w-5" />
                  <span className="text-xs font-bold uppercase tracking-[0.25em]">Comunidad Cofrade</span>
              </div>
              <h1 className="font-headline text-3xl sm:text-4xl font-extrabold tracking-tight bg-gradient-to-r from-white via-white to-primary/70 bg-clip-text text-transparent">
                  Muro Social
              </h1>
              <p className="text-sm text-white/60 max-w-lg">
                  Lo último de hermandades, bandas y organizaciones. Historias, publicaciones y la música que las acompaña.
              </p>
          </div>
      </div>

      {/* ── Search bar ── */}
      <div className="relative" ref={searchRef}>
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
              type="text"
              placeholder="Buscar hermandades, bandas u organizaciones..."
              className="pl-10 h-12 rounded-2xl bg-card/50 border-white/10 placeholder:text-muted-foreground/50 focus:border-primary/40 focus:bg-card/70 transition-all"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => setShowSearchResults(true)}
          />
          {showSearchResults && filteredProfiles.length > 0 && (
              <div className="absolute top-full mt-2 w-full z-10 max-h-80 overflow-y-auto bg-card/90 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl">
                  <div className="p-2 space-y-0.5">
                      {filteredProfiles.map(profile => (
                          <button
                              key={`${profile.type}:${profile.id}`}
                              className="w-full text-left px-3 py-2.5 rounded-xl hover:bg-white/8 flex items-center gap-3 transition-colors"
                              onClick={() => handleProfileClick(profile.type, profile.id)}
                          >
                              <Avatar className="h-8 w-8 border border-white/10">
                                  <AvatarImage src={profile.escudoUrl} />
                                  <AvatarFallback className="text-xs">{(profile.type === 'hermandad' ? (profile as JartinetHermandad).nombre : profile.nombreCompleto).substring(0, 2)}</AvatarFallback>
                              </Avatar>
                              <div>
                                  <p className="font-semibold text-sm">{profile.nombreCompleto || (profile as JartinetHermandad).nombre}</p>
                                  <p className="text-[0.68rem] text-muted-foreground capitalize">{profile.type}</p>
                              </div>
                          </button>
                      ))}
                  </div>
              </div>
          )}
          {showSearchResults && searchQuery && filteredProfiles.length === 0 && (
              <div className="absolute top-full mt-2 w-full z-10 p-4 bg-card/90 backdrop-blur-xl border border-white/10 rounded-2xl shadow-xl">
                  <p className="text-sm text-muted-foreground text-center">No se encontraron perfiles.</p>
              </div>
          )}
      </div>

      {/* El "Salón de la Fama" de me gustas se mudó a /jartinet/hall-of-fame,
          donde vive junto al resto de rankings de la comunidad. */}

      {/* ── Stories bar ── */}
      <div className="relative rounded-2xl bg-card/40 border border-white/8 backdrop-blur-sm overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-primary/5 to-transparent pointer-events-none" />
          <div className="relative px-4 pt-3 pb-1">
              <p className="text-[0.62rem] font-bold uppercase tracking-[0.2em] text-muted-foreground/60">Historias</p>
          </div>
          <div className="relative px-4 pb-3">
              {isLoading ? (
                  <div className="flex items-center gap-3 h-20">
                      <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">Cargando historias…</span>
                  </div>
              ) : stories.length > 0 ? (
                  <div className="flex gap-5 overflow-x-auto pb-1" style={{ scrollbarWidth: 'none' }}>
                      {stories.map((storyGroup) => (
                          <StoryCircle
                              key={storyGroup.id}
                              storyGroup={storyGroup}
                              onClick={() => storyGroup.stories.length > 0 ? handleStoryClick(storyGroup) : handleProfileClick(storyGroup.type, storyGroup.id.split(':')[1])}
                          />
                      ))}
                  </div>
              ) : (
                  <p className="text-sm text-muted-foreground py-4 text-center">No hay historias activas.</p>
              )}
          </div>
      </div>

       {viewerOpen && (
        <StoryViewer
          storyGroups={stories}
          initialGroupIndex={activeStoryGroupIndex}
          onClose={() => setViewerOpen(false)}
          onGroupChange={setActiveStoryGroupIndex}
          onViewPost={(post) => {
            setViewerOpen(false);
            setTimeout(() => {
                setSelectedPost(post);
                setPostViewerOpen(true);
            }, 150)
          }}
          allPosts={allPosts}
        />
      )}
      
      {isHighlightViewerOpen && activeHighlightGroup && (
        <HighlightStoryViewer 
          group={activeHighlightGroup}
          onClose={() => setIsHighlightViewerOpen(false)}
        />
      )}

      {/* ── Feed ── */}
      {isLoading ? (
          <div className="flex justify-center items-center h-64">
              <Loader2 className="h-10 w-10 animate-spin text-primary" />
          </div>
      ) : initialPosts && initialPosts.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
              {initialPosts.map((post, i) => (
                  <motion.div
                      key={post.id}
                      initial={{ opacity: 0, y: 18 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, delay: Math.min(i * 0.04, 0.4), ease: 'easeOut' }}
                  >
                      <PostCard post={post} onHeaderClick={handleProfileClick} currentUser={currentUser} onAction={handleActionCompletion} onShareClick={handleShareClick} />
                  </motion.div>
              ))}
          </div>
      ) : (
          <div className="text-center py-20 border-2 border-dashed border-white/10 rounded-2xl">
              <Newspaper className="mx-auto h-10 w-10 text-muted-foreground mb-3" />
              <h3 className="text-base font-semibold">El muro está vacío</h3>
              <p className="mt-1 text-sm text-muted-foreground">Todavía no hay publicaciones.</p>
          </div>
      )}

      <ProfileSheet
        profile={selectedProfile}
        allPosts={allPosts}
        stories={stories.find(sg => sg.id === `${selectedProfile?.type}:${selectedProfile?.id}`)}
        pendingCollaborations={pendingCollaborations}
        open={profileSheetOpen}
        onOpenChange={setProfileSheetOpen}
        onStoryClick={handleStoryClickFromProfile}
        onHighlightClick={handleHighlightClick}
        onPostClick={handlePostClickFromProfile}
        currentUser={currentUser}
        onAction={handleActionCompletion}
      />

      <PostViewerDialog 
        post={selectedPost}
        open={postViewerOpen}
        onOpenChange={setPostViewerOpen}
        currentUser={currentUser}
        onAction={handleActionCompletion}
        onShareClick={handleShareClick}
      />

      <SharePostDialog
        post={postToShare}
        open={shareDialogOpen}
        onOpenChange={setShareDialogOpen}
        userProfiles={userManagedProfiles}
        onAction={handleActionCompletion}
      />

      <AlertDialog open={citizenDialogOpen} onOpenChange={setCitizenDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2"><Lock className="h-5 w-5 text-primary" /> Solo para ciudadanos de Jartiland</AlertDialogTitle>
            <AlertDialogDescription>
              Estás viendo la red social como visitante. Para dar me gusta, comentar, compartir o publicar necesitas ser <strong>ciudadano de Jartiland</strong>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Seguir mirando</AlertDialogCancel>
            <AlertDialogAction asChild>
              <Link to="/apply">Quiero ser ciudadano</Link>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
   </SocialContext.Provider>
  );
}
